import React from "react";
import { TrendingUp, TrendingDown, Sparkles, ArrowRight } from "lucide-react";
import SignalDot from "./SignalDot";

/**
 * Cinematic "Top setup right now" featured card.
 * Animated glowing border, big score, key indicators.
 */
export default function TopSetup({ setup, onOpen, onAddWatchlist }) {
  if (!setup) {
    return (
      <div className="card-surface p-6 relative overflow-hidden" data-testid="top-setup-empty">
        <div className="label-mono">Top Setup</div>
        <div className="text-white/50 text-sm mt-2">Run a scan to surface the strongest live setup.</div>
      </div>
    );
  }
  const isBull = setup.direction === "bullish";
  const accent = isBull ? "var(--bull)" : "var(--bear)";
  const glow = isBull ? "rgba(0,255,156,0.45)" : "rgba(255,77,109,0.45)";

  return (
    <div
      className="relative rounded-2xl p-[1.5px] fade-in glow-border"
      style={{ "--glow-color": accent }}
      data-testid="top-setup-card"
    >
      <div className="relative rounded-2xl glass-strong overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{ background: `radial-gradient(700px 220px at 0% 0%, ${isBull ? "rgba(0,255,156,0.18)" : "rgba(255,77,109,0.18)"}, transparent 60%)` }}
        />
        <div className="relative p-5 sm:p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <SignalDot variant={isBull ? "bull" : "bear"} strength="strong" />
              <div className="label-mono">Top setup right now</div>
              <span className={`signal-pill ${isBull ? "bull" : "bear"}`}>
                {isBull ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {isBull ? "BULLISH" : "BEARISH"}
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-4 flex-wrap">
              <div className="text-3xl sm:text-4xl text-white font-semibold tracking-[-0.02em]">{setup.ticker}</div>
              <div className="text-white/45 text-sm tracking-tight truncate max-w-[260px]">{setup.name}</div>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2.5">
              <Stat label="Price" value={setup.price.toFixed(2)} />
              <Stat label="Chg %" value={`${setup.change_pct >= 0 ? "+" : ""}${setup.change_pct.toFixed(2)}%`} cls={setup.change_pct >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
              <Stat label="RSI" value={setup.indicators.rsi.toFixed(1)} />
              <Stat label="MACD" value={setup.indicators.macd_hist >= 0 ? "Bullish" : "Bearish"} cls={setup.indicators.macd_hist >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
              <Stat label="TF" value={setup.timeframe.toUpperCase()} />
            </div>
          </div>
          <div className="flex flex-col items-center gap-3 md:border-l md:border-white/5 md:pl-6">
            <div className="label-mono">Strength</div>
            <div className="relative">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" stroke="rgba(255,255,255,0.06)" strokeWidth="8" fill="none" />
                <circle
                  cx="60" cy="60" r="50" fill="none"
                  stroke={accent} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(setup.score / 100) * 314} 314`}
                  transform="rotate(-90 60 60)"
                  style={{ filter: `drop-shadow(0 0 12px ${glow})`, transition: "stroke-dasharray 0.6s ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-3xl text-white font-semibold tabular-nums">{setup.score}</div>
                <div className="label-mono mt-0.5">/ 100</div>
              </div>
            </div>
            <div className="flex gap-2 mt-1">
              <button onClick={() => onOpen && onOpen(setup.ticker)} className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5" data-testid="top-setup-open">
                Open chart <ArrowRight className="w-3 h-3" />
              </button>
              <button onClick={() => onAddWatchlist && onAddWatchlist(setup)} className="btn-ghost text-xs px-3 py-2 flex items-center gap-1.5" data-testid="top-setup-watch">
                <Sparkles className="w-3 h-3" /> Watch
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, cls }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2">
      <div className="label-mono">{label}</div>
      <div className={`font-mono text-[14px] mt-0.5 tabular-nums ${cls || "text-white"}`}>{value}</div>
    </div>
  );
}
